import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import RecordsCards from "@/components/RecordsCards";
import { toast } from "sonner";
import {
  Trophy, Target, ChefHat, Sparkles, TrendingUp, TrendingDown, Award, Rocket,
  Sunrise, Sunset, Utensils, BarChart3, ArrowRight, Crown, ChevronLeft, ChevronRight,
  AlertTriangle, Bell, Search, ChevronDown, CheckCircle2, Circle, Clock,
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";

const RIT_ICONS = { sunrise: Sunrise, sunset: Sunset, chart: BarChart3, utensils: Utensils };
const eur = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const pct = (v, d = 1) => v == null ? "—" : `${v.toFixed(d)}%`;
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

/* ─── Small circular gauge ───────────────────────────────────── */
function MiniGauge({ value, size = 72, thickness = 8, tone = "ok" }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const dash = (c * v) / 100;
  const colors = { ok: "#10b981", near: "#f59e0b", ko: "#ef4444" };
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={thickness}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={colors[tone] || colors.ok}
                strokeWidth={thickness} strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
                style={{ transition: "stroke-dasharray 700ms cubic-bezier(.4,0,.2,1)" }}/>
      </svg>
      <div className="absolute inset-0 grid place-items-center text-sm font-bold text-slate-900">{Math.round(v)}%</div>
    </div>
  );
}

/* ─── Store selector (managers only) ─────────────────────────── */
function StoreSelector({ user, currentStore, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const isCorner = user?.is_corner || user?.role === "corner";

  useEffect(() => {
    if (isCorner || !open) return;
    const t = setTimeout(() => {
      api.get("/corner/stores-search", { params: { q, limit: 15 } })
        .then(r => setResults(r.data || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, isCorner]);

  if (isCorner) {
    return (
      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white grid place-items-center font-black">{(currentStore?.name || "M")[0]}</div>
        <div>
          <div className="font-display font-bold text-slate-900 text-sm">{currentStore?.name}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Magasin</div>
        </div>
      </div>
    );
  }
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
              data-testid="store-selector-btn"
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3 hover:border-blue-300 min-w-[240px]">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white grid place-items-center font-black">{(currentStore?.name || "M")[0]}</div>
        <div className="flex-1 text-left">
          <div className="font-display font-bold text-slate-900 text-sm">{currentStore?.name || "Sélectionner…"}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Magasin</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden" data-testid="store-selector-panel">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input data-testid="store-search-input" value={q} onChange={e => setQ(e.target.value)}
                     placeholder="Rechercher un magasin…" autoFocus
                     className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400"/>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {results.length === 0 && <div className="px-3 py-4 text-xs text-slate-400 text-center">Tapez pour rechercher</div>}
            {results.map(s => (
              <button key={s.id} onClick={() => { onSelect(s); setOpen(false); setQ(""); }}
                      data-testid={`store-result-${s.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left border-t border-slate-50">
                <div className="w-7 h-7 rounded-full bg-slate-100 grid place-items-center text-slate-700 text-xs font-bold">{(s.name || "?")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{s.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{s.enseigne} · {s.sector_zone || s.region_name || "—"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── KPI card with mini-gauge ───────────────────────────────── */
function KpiCard({ label, value, objective, unit, tone, testid, delta }) {
  const pctVal = objective != null && objective > 0 ? Math.min(999, (value / objective) * 100) : 0;
  const displayVal = value == null ? "—" :
    (unit === "€" ? eur(value) : unit === "%" ? `${(value).toFixed(1)}%` : unit === "/100" ? `${Math.round(value)} /100` : Math.round(value));
  const displayObj = objective == null ? "—" :
    (unit === "€" ? eur(objective) : unit === "%" ? `${objective.toFixed(1)}%` : unit === "/100" ? `${Math.round(objective)}` : objective);
  const gaugeTone = tone === "ok" ? "ok" : tone === "near" ? "near" : "ko";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 relative" data-testid={testid}>
      {tone === "ko" && (
        <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Attention</span>
      )}
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="flex items-end gap-3 mt-2">
        <div className="flex-1">
          <div className="font-display font-black text-2xl text-slate-900">{displayVal}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Objectif : {displayObj}</div>
        </div>
        <MiniGauge value={pctVal > 100 && tone === "ko" ? -Math.round(pctVal - 100) : pctVal} tone={gaugeTone}/>
      </div>
      {delta != null && (
        <div className="mt-2 text-[11px] text-slate-500">
          vs hier{" "}
          <span className={`font-bold ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-500"}`}>
            {delta > 0 ? "+" : ""}{delta.toFixed(1)}{unit === "%" || unit === "/100" ? " pts" : "%"}
          </span>
        </div>
      )}
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────── */
export default function KpiCorner() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [weekLoading, setWeekLoading] = useState(false);

  const fetchDashboard = () => {
    const params = {};
    if (weekStart) params.week_start = weekStart;
    if (selectedStore?.id) params.store_id = selectedStore.id;
    setWeekLoading(true);
    api.get("/kpi/corner-dashboard", { params })
      .then(r => setD(r.data))
      .catch(() => setD(null))
      .finally(() => { setLoading(false); setWeekLoading(false); });
  };
  useEffect(() => { fetchDashboard(); /* eslint-disable-next-line */ }, [weekStart, selectedStore]);

  const toggleAction = async (id) => {
    try {
      await api.post(`/plans-action/${id}/toggle`);
      fetchDashboard();
    } catch (e) { toast.error("Erreur"); }
  };

  if (loading) return <div className="text-slate-400 text-center py-16">Chargement de votre tableau de bord…</div>;
  if (!d) return (
    <div className="max-w-md mx-auto text-center py-16">
      <div className="text-6xl mb-4">🍣</div>
      <div className="font-display text-xl font-bold text-slate-800">Bienvenue !</div>
      <div className="text-sm text-slate-500 mt-2">Ce dashboard est réservé aux comptes magasin ou aux managers.</div>
    </div>
  );

  const { store, wasascore, primes, ca_week, rituels, actions_du_jour, actions_stats, alertes_recent, alertes_total, champions, badges, message_of_day, period, week_nav, chat_id } = d;
  const ritDone = rituels.filter(r => r.done).length;
  const historyData = wasascore.history_7d || [];

  const RIT_PREFILL = {
    vitrine_matin: "Voici la vitrine du matin 📷",
    ca_hier: "CA d'hier : ",
    plateau_midi: "Voici le plateau du midi 🍣",
    ca_pm: "CA 14h : ",
    vitrine_pm: "Voici la vitrine de l'après-midi 📷",
  };
  const openRituelInChat = (rit) => {
    if (rit.done) return;
    const qs = new URLSearchParams({ tab: "messagerie", chat: chat_id || "",
                                     rituel: rit.key, prefill: RIT_PREFILL[rit.key] || rit.label });
    window.location.assign(`/journal?${qs.toString()}`);
  };

  const priorityBadge = (p) => ({
    urgent: "bg-rose-100 text-rose-700",
    important: "bg-amber-100 text-amber-700",
    normal: "bg-slate-100 text-slate-600",
  }[p] || "bg-slate-100 text-slate-600");
  const priorityLabel = (p) => ({ urgent: "Urgent", important: "Important", normal: "" }[p] || "");

  const alertLevelBadge = (lvl) => ({
    critique: "bg-rose-100 text-rose-700 border-rose-200",
    important: "bg-amber-100 text-amber-700 border-amber-200",
    a_traiter: "bg-yellow-100 text-yellow-700 border-yellow-200",
  }[lvl] || "bg-slate-100 text-slate-600 border-slate-200");
  const alertLevelLabel = (lvl) => ({
    critique: "Critique", important: "Important", a_traiter: "À traiter",
  }[lvl] || "");

  const isCorner = user?.is_corner || user?.role === "corner";
  const displayStore = selectedStore || store;
  const wasaTarget = wasascore.target || 90;
  const wasaDelta = wasascore.delta_yesterday || 0;
  const wasaEcart = Math.max(0, wasaTarget - wasascore.score);

  const totalCA = ca_week.reduce((s, c) => s + (c.realise || 0), 0);
  const totalObj = ca_week.reduce((s, c) => s + (c.objectif || 0), 0);
  const caPctSemaine = totalObj > 0 ? Math.round((totalCA / totalObj) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="kpi-corner-page">
      {/* Top bar : greeting + store selector + message du jour */}
      {/* Records injectés plus bas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-2">
          <div>
            <div className="font-display text-2xl font-bold text-slate-900" data-testid="corner-title">Bonjour équipe ! 👋</div>
            <div className="text-sm text-slate-500">Prêts à faire une excellente journée ? 💪</div>
          </div>
          <StoreSelector user={user} currentStore={displayStore} onSelect={setSelectedStore}/>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 text-white relative overflow-hidden" data-testid="message-of-day">
          <div className="absolute -top-4 -right-4 text-6xl opacity-20">✨</div>
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Le message du jour</div>
          <div className="text-sm font-semibold mt-1 leading-snug">{message_of_day?.message}</div>
          {message_of_day?.author_name && (
            <div className="text-[10px] mt-2 opacity-80">— {message_of_day.author_name}</div>
          )}
        </div>
      </div>

      {/* Hero WasaScore + Actions du jour */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-5 text-white relative overflow-hidden" data-testid="wasascore-hero">
          <div className="absolute -top-8 -right-8 text-9xl opacity-10">🏆</div>
          <div className="relative flex flex-col md:flex-row gap-4 items-start">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">Wasa Score</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="font-display text-5xl font-black" data-testid="wasa-score-value">{wasascore.score}</div>
                <div className="text-lg opacity-90">/100</div>
                {wasaDelta !== 0 && (
                  <div className={`text-xs font-bold px-2 py-1 rounded-full ${wasaDelta > 0 ? "bg-emerald-400/25" : "bg-rose-400/25"}`}>
                    {wasaDelta > 0 ? "↗" : "↘"} {wasaDelta > 0 ? "+" : ""}{wasaDelta} pts vs hier
                  </div>
                )}
              </div>
              {wasascore.rank && (
                <div className="mt-2 flex items-center gap-2">
                  <Crown className="w-4 h-4"/>
                  <span className="text-sm font-semibold" data-testid="wasa-rank">Rang <b>{wasascore.rank}<sup>e</sup></b> sur {wasascore.total} corners</span>
                </div>
              )}
              <Link to="/classement" data-testid="see-ranking"
                    className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 rounded-full bg-white/25 hover:bg-white/40 text-xs font-bold backdrop-blur">
                Voir le détail du score <ArrowRight className="w-3 h-3"/>
              </Link>
            </div>
            <div className="w-full md:w-56 h-24" data-testid="wasa-history-chart">
              <div className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-1 text-right">Objectif : {wasaTarget}</div>
              <ResponsiveContainer>
                <AreaChart data={historyData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="wasaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity={0.7}/>
                      <stop offset="100%" stopColor="#ffffff" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <YAxis hide domain={[0, 100]}/>
                  <XAxis hide dataKey="date"/>
                  <Tooltip contentStyle={{ background: "rgba(15,23,42,0.9)", border: "none", borderRadius: 6, color: "#fff", fontSize: 11 }}/>
                  <ReferenceLine y={wasaTarget} stroke="#fef3c7" strokeDasharray="3 3"/>
                  <Area type="monotone" dataKey="score" stroke="#ffffff" strokeWidth={2} fill="url(#wasaGrad)"/>
                </AreaChart>
              </ResponsiveContainer>
              <div className="text-[10px] text-right opacity-70 mt-1">Évolution 7 derniers jours</div>
            </div>
          </div>
        </div>
        {/* Actions du jour */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="actions-du-jour-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-slate-800">Actions du jour <span className="text-slate-400 font-normal text-xs">({actions_stats.total})</span></div>
            <div className={`text-xs font-bold px-3 py-1 rounded-full ${actions_stats.done === actions_stats.total && actions_stats.total > 0 ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700"}`}>
              {actions_stats.done}/{actions_stats.total || 0}
            </div>
          </div>
          {actions_du_jour.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-8 h-8 mx-auto text-slate-300 mb-1"/>
              <div className="text-xs text-slate-400">Aucune action affectée pour le moment.</div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {actions_du_jour.slice(0, 6).map(a => (
                <button key={a.id} onClick={() => toggleAction(a.id)}
                        data-testid={`action-${a.id}`}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg border transition ${a.done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 hover:bg-white"}`}>
                  {a.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0"/> : <Circle className="w-4 h-4 text-slate-300 shrink-0"/>}
                  <span className={`flex-1 text-left text-xs font-medium ${a.done ? "text-slate-400 line-through" : "text-slate-800"}`}>{a.title}</span>
                  {a.priority !== "normal" && !a.done && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${priorityBadge(a.priority)}`}>{priorityLabel(a.priority)}</span>
                  )}
                  {a.hour && <span className="text-[10px] text-slate-500 font-mono">{a.hour}</span>}
                </button>
              ))}
            </div>
          )}
          <div className="mt-3">
            <Link to="/ops/interventions" className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
              Voir toutes les actions <ArrowRight className="w-3 h-3"/>
            </Link>
          </div>
        </div>
      </div>

      {/* Records & séries */}
      <RecordsCards storeId={displayStore?.id} />

      {/* Mes indicateurs clés */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-800">Mes indicateurs clés</h2>
          <div className="text-xs text-slate-500">{period?.label}</div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
          <KpiCard label="CA jour" value={primes.ca.realise} objective={primes.ca.objectif} unit="€"
                   tone={primes.ca.status} testid="kpi-ca"/>
          <KpiCard label="Foodcost" value={primes.foodcost.realise} objective={primes.foodcost.objectif} unit="%"
                   tone={primes.foodcost.status} testid="kpi-foodcost"/>
          <KpiCard label="Hygiène" value={primes.hygiene.realise} objective={primes.hygiene.objectif} unit="/100"
                   tone={primes.hygiene.status} testid="kpi-hygiene"/>
          <KpiCard label="Casse" value={primes.casse.realise} objective={primes.casse.objectif} unit="%"
                   tone={primes.casse.status} testid="kpi-casse"/>
        </div>
      </div>

      {/* Rituels + CA semaine + Champions + Alertes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Rituels du jour */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="rituels-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-slate-800 text-sm">Les rituels du jour</div>
            <div className={`text-xs font-bold px-3 py-1 rounded-full ${ritDone === 5 ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700"}`} data-testid="rituels-count">
              {ritDone}/5
            </div>
          </div>
          <div className="grid grid-cols-5 gap-1">
            {rituels.map(r => {
              const Icon = RIT_ICONS[r.icon] || Trophy;
              return (
                <button key={r.key} type="button" onClick={() => openRituelInChat(r)} disabled={r.done}
                        data-testid={`rituel-btn-${r.key}`}
                        className={`relative flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                          r.done ? "bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-md cursor-default"
                                 : "bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-700 hover:scale-105 active:scale-95 cursor-pointer"}`}>
                  <div className={`w-8 h-8 grid place-items-center rounded-full ${r.done ? "bg-white/25" : "bg-white"}`}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="text-[9px] font-bold uppercase text-center leading-tight">{r.label}</div>
                  {r.done && <span className="absolute -top-1 -right-1 text-xs">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-3">
            <button onClick={() => window.location.assign(`/journal?tab=messagerie&chat=${chat_id||""}`)}
                    className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
              Accéder aux rituels <ArrowRight className="w-3 h-3"/>
            </button>
          </div>
        </div>

        {/* CA semaine chart */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="ca-week-card">
          <div className="flex items-center justify-between mb-2 gap-1">
            <div className="font-display font-bold text-slate-800 text-sm">CA semaine (réalisé / objectif)</div>
            <div className="flex items-center gap-1">
              <button data-testid="week-prev-btn" onClick={() => setWeekStart(week_nav?.prev)} disabled={weekLoading}
                      className="w-7 h-7 grid place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-100 text-slate-600 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4"/>
              </button>
              <button data-testid="week-next-btn" onClick={() => setWeekStart(week_nav?.next)} disabled={weekLoading}
                      className="w-7 h-7 grid place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-100 text-slate-600 disabled:opacity-40">
                <ChevronRight className="w-4 h-4"/>
              </button>
            </div>
          </div>
          <div className="text-[10px] font-semibold text-slate-500 mb-2" data-testid="week-label">{week_nav?.label}</div>
          <div className="h-32">
            <ResponsiveContainer>
              <BarChart data={ca_week.map(c => ({...c, tone: c.realise >= c.objectif ? "#10b981" : c.realise >= c.objectif*0.85 ? "#f59e0b" : "#ef4444"}))}>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 11 }} formatter={(v) => eur(v)}/>
                <Bar dataKey="objectif" fill="#e2e8f0" radius={[4,4,0,0]} barSize={12}/>
                <Bar dataKey="realise" fill="#3b82f6" radius={[4,4,0,0]} barSize={12}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-600 mt-1 pt-2 border-t border-slate-100">
            <span>{eur(totalCA)} <span className="text-slate-400">sur {eur(totalObj)}</span></span>
            <span className={`font-bold ${caPctSemaine >= 100 ? "text-emerald-600" : caPctSemaine >= 85 ? "text-amber-600" : "text-rose-600"}`}>{caPctSemaine}%</span>
          </div>
        </div>

        {/* Champions */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="champions-card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-amber-500"/>
            <div className="font-display font-bold text-slate-800 text-sm">Champions de la semaine</div>
          </div>
          <div className="space-y-2">
            {champions.slice(0, 3).map((c, i) => {
              const myScore = c.score;
              const isMe = c.corner_key && ck_included(c.corner_key, store);
              return (
                <div key={c.stand} className={`flex items-center gap-2 p-2 rounded-lg ${isMe ? "bg-blue-50 ring-1 ring-blue-200" : "bg-slate-50"}`} data-testid={`champion-${i}`}>
                  <div className={`w-6 h-6 grid place-items-center rounded-full text-white text-[10px] font-bold ${i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : "bg-orange-400"}`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-900 truncate">{c.stand}</div>
                  </div>
                  <div className="text-xs font-bold text-emerald-600">{myScore?.toFixed?.(0) || "—"} pts</div>
                </div>
              );
            })}
            {champions.length === 0 && <div className="text-xs text-slate-400 italic">En attente…</div>}
          </div>
          <div className="mt-3">
            <Link to="/classement" className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
              Voir tout le classement <ArrowRight className="w-3 h-3"/>
            </Link>
          </div>
        </div>
      </div>

      {/* Alertes & points d'attention */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="alertes-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500"/>
            <div className="font-display font-bold text-slate-800">Alertes & points d'attention</div>
          </div>
          <span className="text-xs text-slate-500">{alertes_total} active{alertes_total > 1 ? "s" : ""}</span>
        </div>
        {alertes_recent.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-400 mb-1"/>
            <div className="text-xs text-slate-500">Aucune alerte active. Continuez comme ça ! 🎉</div>
          </div>
        ) : (
          <div className="space-y-2">
            {alertes_recent.map(a => (
              <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 bg-slate-50" data-testid={`alerte-item-${a.id}`}>
                <div className="w-9 h-9 grid place-items-center rounded-full bg-white text-slate-600 shrink-0"><Bell className="w-4 h-4"/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{a.description}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{a.type}</div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${alertLevelBadge(a.level)}`}>{alertLevelLabel(a.level)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3">
          <Link to={isCorner ? "/alertes-terrain" : "/ops/alertes"} className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
            Voir toutes les alertes <ArrowRight className="w-3 h-3"/>
          </Link>
        </div>
      </div>

      {/* Bandeau progression bas */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-4 text-white relative overflow-hidden" data-testid="progress-band">
        <div className="absolute -top-4 -right-4 text-8xl opacity-10">🚀</div>
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4 items-center">
          <div className="flex items-center gap-3">
            <Rocket className="w-8 h-8"/>
            <div>
              <div className="font-display text-sm font-bold">Encore un effort !</div>
              <div className="text-[11px] opacity-90">Vous êtes à {wasaEcart.toFixed(1)} points de votre objectif ⭐</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Objectif Wasa Score</div>
            <div className="font-display text-2xl font-black mt-0.5">{wasaTarget} <span className="text-sm opacity-80">/100</span></div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Rituels complétés</div>
            <div className="font-display text-2xl font-black mt-0.5">{ritDone} <span className="text-sm opacity-80">/5</span></div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Actions terminées</div>
            <div className="font-display text-2xl font-black mt-0.5">{actions_stats.done} <span className="text-sm opacity-80">/{actions_stats.total || 0}</span></div>
          </div>
        </div>
        {badges.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/20 relative" data-testid="badges-row">
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-2">Vos badges</div>
            <div className="flex items-center gap-3 flex-wrap">
              {badges.map((b, i) => (
                <div key={b.key || i} className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5 backdrop-blur">
                  <span className="text-lg">{b.icon || "🏅"}</span>
                  <span className="text-xs font-semibold">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helpers
function ck_included(ck, store) {
  if (!ck || !store) return false;
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const target = norm(ck);
  return [store.name, store.canonical_name].some(n => norm(n).includes(target) || target.includes(norm(n)));
}
